import { isScreenshotMode } from "@constants/common";
import { mockedProfileDetails } from "@constants/mocked-data";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getParentMetadata } from "@helpers/content/metadata";
import { useAppStore } from "@hooks/useAppStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { LeaderboardRecord } from "@memorang/types";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useTranslation } from "react-i18next";
import { getHeaderStats, getStatsSectionData } from "../helpers/profile-data";
import { useUserLeaderboard } from "./useUserLeaderboard";

const emptyLeaderboardRecord: LeaderboardRecord = {
	xp: 0,
	rank: 0,
	username: "",
	avatar: "",
	level: 0,
};

const useProfileDetails = () => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const { currentExamContext } = useExamContext();
	const { examDate } = currentExamContext;
	const exam = useAppStore((store) => store.exam);

	const allTimeQuery = useUserLeaderboard({ timePeriod: "all_time" });
	const monthQuery = useUserLeaderboard({ timePeriod: "month" });
	const weekQuery = useUserLeaderboard({ timePeriod: "week" });
	const { data: commonDistribution, isLoading: isDistributionLoading } =
		useMixedItemDist();

	const isLoading =
		allTimeQuery.isLoading ||
		monthQuery.isLoading ||
		weekQuery.isLoading ||
		isDistributionLoading;

	const allTimeLeaderboard = allTimeQuery.data ?? emptyLeaderboardRecord;
	const monthLeaderboard = monthQuery.data ?? emptyLeaderboardRecord;
	const weekLeaderboard = weekQuery.data ?? emptyLeaderboardRecord;

	const { distribution } = getParentMetadata(
		commonDistribution?.contents || [],
	);

	const low = distribution.low || 0;
	const high = distribution.high || 0;
	const medium = distribution.medium || 0;
	const numItemsAnswered = low + high + medium;
	const accuracy = numItemsAnswered
		? Math.round(((high + medium) / numItemsAnswered) * 100)
		: 0;

	const headerStats = getHeaderStats({
		level: allTimeLeaderboard.level,
		accuracy,
		answered: numItemsAnswered,
		theme,
	});
	const statsTabData = getStatsSectionData({
		selectedBundleContext: currentExamContext,
		selectedDate: examDate!,
		allTimeLeaderboard,
		monthLeaderboard,
		weekLeaderboard,
		examEnabled: exam?.enabled,
		t,
	});

	const profileDetails = isLoading
		? undefined
		: {
				headerStats,
				statsTabData,
			};

	return {
		profileDetails: isScreenshotMode ? mockedProfileDetails : profileDetails,
	};
};

export default useProfileDetails;
